import React, { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import Spinner from '../../components/Spinner/Spinner';

import { trackPathRecommendationViewedEvent } from '../../services/DataPipeline/DataPipeline';
import { PATH_RECOMMENDATION_CONTEXT } from '../../services/DataPipeline/models';
import { navigateWithReload } from '../../utils';
import { ROUTES } from '../../utils/routes';
import { STORAGE_KEYS } from '../../utils/storage';
import { useProgramData } from '../../hooks/onboarding/useProgramData';
import { ProgramErrorState } from './ProgramErrorState';
import { ProgramEmptyState } from './ProgramEmptyState';
import { ProgramDisplay } from './ProgramDisplay';
import { SUBMIT_SURVEY_ANSWER } from '../../services/ApolloClient/Mutations';
import { ErrorsService } from '../../services/ErrorsService';
import { OnboardingStorageData } from '../../types/onboarding/survey';
import './RecommendedProgram.scss';

export const RecommendedProgram: React.FC = () => {
  const hasTrackedEvent = useRef(false);
  const hasSubmittedSurvey = useRef(false);
  const { isLoading, hasError, errorMessage, hasData, programData, fallbackData, handleRetry } =
    useProgramData();

  const [submitSurveyMutation] = useMutation(SUBMIT_SURVEY_ANSWER);

  // Extract submission logic into a separate function (easy to move to button handler if needed)
  const submitSurveyAnswers = useCallback(async () => {
    if (hasSubmittedSurvey.current) {
      return; // Prevent double submission
    }

    try {
      const onboardingDataStr = localStorage.getItem(STORAGE_KEYS.ONBOARDING_DATA);

      if (onboardingDataStr) {
        const onboardingData: OnboardingStorageData = JSON.parse(onboardingDataStr);

        if (onboardingData.surveyId && onboardingData.answers) {
          await submitSurveyMutation({
            variables: {
              surveyId: onboardingData.surveyId,
              answer: JSON.stringify(onboardingData.answers),
            },
            fetchPolicy: 'no-cache',
          });
          hasSubmittedSurvey.current = true;
        }
      }
    } catch (error) {
      ErrorsService.message(
        `[RecommendedProgram] Error submitting survey: ${
          error instanceof Error ? error.message : String(error)
        }`,
        { extra: { error } },
      );
      // Continue silently - don't block user experience as survey will be shown again in postpurchase flow
    }
  }, [submitSurveyMutation]);

  // Auto-submit survey answers when component is ready
  useEffect(() => {
    if (!isLoading && hasData && !hasSubmittedSurvey.current) {
      submitSurveyAnswers();
    }
  }, [isLoading, hasData, submitSurveyAnswers]);

  // Track Path Recommendation Viewed event
  useEffect(() => {
    if (isLoading || !hasData || hasTrackedEvent.current) {
      return;
    }

    const pathName = programData?.name || fallbackData?.title;

    if (pathName) {
      trackPathRecommendationViewedEvent({
        path_recommendation_context: PATH_RECOMMENDATION_CONTEXT.PRE_PAYWALL_ONBOARDING,
        path_recommended: pathName,
      });

      hasTrackedEvent.current = true;
    }
  }, [isLoading, hasData, programData, fallbackData]);

  if (isLoading) {
    return <Spinner isDark />;
  }

  const handleContinue = () => {
    localStorage.removeItem(STORAGE_KEYS.ONBOARDING_DATA);
    navigateWithReload(ROUTES.JOIN);
  };

  const handleStartOver = () => {
    localStorage.removeItem(STORAGE_KEYS.ONBOARDING_DATA);
    navigateWithReload(ROUTES.ONBOARDING);
  };

  const imageUrl = programData?.image || fallbackData?.image || '';
  const title = programData?.name || fallbackData?.title || '';
  const description = programData?.description || fallbackData?.joinPageDescription || '';

  if (hasError) {
    return (
      <ProgramErrorState
        errorMessage={errorMessage}
        onRetry={handleRetry}
        onStartOver={handleStartOver}
      />
    );
  }

  if (!hasData) {
    return <ProgramEmptyState onStartOver={handleStartOver} />;
  }

  return (
    <ProgramDisplay
      title={title}
      description={description}
      imageUrl={imageUrl}
      onContinue={handleContinue}
      onStartOver={handleStartOver}
    />
  );
};
